import {
  Component,
  computed,
  effect,
  inject,
  OnInit,
  signal,
} from "@angular/core";
import { TileInterface } from "src/app/core/interfaces/tile-interface";
import { Tile } from "src/app/shared/components/tile/tile";
import { ThemedSection } from "src/app/shared/components/utils/themed-section/themed-section";
import { Loader } from "src/app/shared/components/loader/loader";
import { TilesApiService } from "src/app/core/services/tiles/tiles-service";
import { TranslocoModule } from "@jsverse/transloco";
import { Toggle } from "src/app/shared/components/toggle/toggle";
import { UserStore } from "../../store/user-store";
import { LanguageService } from "src/app/core/services/language-service";
import { FavoriteTileErrorModal } from "src/app/shared/components/favorite-tile-error-modal/favorite-tile-error-modal";
import { ErrorModalService } from "src/app/core/services/error-modal-service";
import { PianoAnalyticsService } from "src/app/core/services/piano-analytics";

@Component({
  selector: "app-development-page",
  imports: [
    Toggle,
    Tile,
    ThemedSection,
    Loader,
    TranslocoModule,
    FavoriteTileErrorModal,
  ],
  templateUrl: "./development-page.html",
})
export class DevelopmentPage implements OnInit {
  private readonly tilesApi = inject(TilesApiService);
  private readonly piano = inject(PianoAnalyticsService);
  private readonly devThemeLabel = "Me Développer";
  private readonly aboutMeLabel = "Mieux me connaître";

  private readonly languageService = inject(LanguageService);
  private readonly errorModalService = inject(ErrorModalService);

  loading = signal<boolean>(false);
  modeDraft = signal<boolean>(false);
  tilesDevelopment: TileInterface[] = [];
  tilesDevelopmentToShow = signal<TileInterface[]>([]);
  tilesAboutMe: TileInterface[] = [];
  tilesAboutMeToShow = signal<TileInterface[]>([]);
  userStore = inject(UserStore);
  userRole = computed(() => this.userStore.getUserPermission());

  constructor() {
    effect(() => {
      this.languageService.currentLanguage();
      this.loadDevelopmentTiles();
    });
  }

  ngOnInit(): void {
    this.piano.trackPageDisplay("development", "/development");
  }

  private async loadDevelopmentTiles(): Promise<void> {
    this.loading.set(true);
    try {
      const tiles = await this.tilesApi.getTilesDevelopment();

      this.tilesDevelopment = tiles
        .filter(
          (tile) =>
            this.normalizeTheme(tile.developmentTheme) ===
            this.normalizeTheme(this.devThemeLabel)
        )
        .map((tile, idx) => this.tilesApi.mapTileToTileInterface(tile, idx));

      this.tilesAboutMe = tiles
        .filter(
          (tile) =>
            this.normalizeTheme(tile.developmentTheme) ===
            this.normalizeTheme(this.aboutMeLabel)
        )
        .map((tile, idx) => this.tilesApi.mapTileToTileInterface(tile, idx));
      this.updateTilesToShow();
    } catch (error) {
      if (this.errorModalService.shouldOpenErrorModal(error)) {
        this.errorModalService.openErrorModal();
      }
    } finally {
      this.loading.set(false);
    }
  }

  private normalizeTheme(theme: string): string {
    return theme
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase();
  }

  toggleModeDraft(): void {
    this.modeDraft.set(!this.modeDraft());
    this.updateTilesToShow();
  }

  updateTilesToShow() {
    this.tilesApi.updateTilesToShow(
      this.tilesDevelopment,
      this.tilesDevelopmentToShow,
      this.modeDraft()
    );
    this.tilesApi.updateTilesToShow(
      this.tilesAboutMe,
      this.tilesAboutMeToShow,
      this.modeDraft()
    );
  }
}
